import { oids } from "@/configs/Settings";
import { GetRelatedPerson } from "@/services/fhir/relatedperson/GetRelatedPerson";
import { normalizeCpf } from "src-next/shared/utils/cpf";

import type { CrmPatientSummary } from "../types";

type RelatedPersonBundleEntry = {
  resource?: {
    id?: string;
    patient?: { reference?: string };
    name?: Array<{ use?: string; text?: string }>;
    telecom?: Array<{ system?: string; value?: string }>;
    identifier?: Array<{ system?: string; value?: string }>;
  };
};

function pickPreferredNameText(name?: Array<{ use?: string; text?: string }>) {
  const list = name ?? [];
  const official = list.find((n) => n.use === "official")?.text?.trim();
  if (official) return official;
  const usual = list.find((n) => n.use === "usual")?.text?.trim();
  if (usual) return usual;
  return list[0]?.text?.trim() ?? "";
}

function pickTutorCpfFromIdentifiers(
  identifiers: Array<{ system?: string; value?: string }> | undefined,
): string {
  const list = identifiers ?? [];
  const byCpfOid = list.find(
    (row) => String(row.system ?? "").trim() === oids.cpf,
  );
  const preferred = String(byCpfOid?.value ?? "").trim();
  if (preferred) return preferred;
  for (const row of list) {
    const v = String(row.value ?? "").trim();
    if (v) return v;
  }
  return "";
}

/** ID lógico do Patient por CPF (`2.16.840.1.113883.13.237-12345678901`). */
export function buildLogicalPatientIdFromCpfDigits(cpfDigits: string): string {
  return `${oids.cpf}-${normalizeCpf(cpfDigits)}`;
}

/**
 * Extrai CPF (11 dígitos) de um `patientId` ou referência FHIR de Patient lógico.
 */
export function extractCpfDigitsFromPatientId(patientId: string): string | undefined {
  const trimmed = String(patientId ?? "").trim();
  if (!trimmed) return undefined;

  if (trimmed.includes(`${oids.cpf}-`)) {
    const suffix = trimmed.split(`${oids.cpf}-`).pop() ?? "";
    const digits = normalizeCpf(suffix);
    return digits.length === 11 ? digits : undefined;
  }

  const digits = normalizeCpf(trimmed);
  return digits.length === 11 ? digits : undefined;
}

/** Destinatário vet enviado via FluxZap costuma ser o Patient lógico por CPF do tutor. */
export function isLogicalCpfPatientId(patientId: string): boolean {
  return Boolean(extractCpfDigitsFromPatientId(patientId));
}

function mapRelatedPersonToTutorSummary(params: {
  relatedPerson: RelatedPersonBundleEntry["resource"];
  lookupPatientId: string;
}): CrmPatientSummary | null {
  const rp = params.relatedPerson;
  if (!rp) return null;

  const fullName = pickPreferredNameText(rp.name) || "Sem nome";
  const phone =
    rp.telecom?.find((t) => t.system === "phone")?.value?.trim() ?? undefined;
  const cpfRaw = pickTutorCpfFromIdentifiers(rp.identifier);
  const cpfDigits = cpfRaw ? normalizeCpf(cpfRaw) : "";
  const taxIdentifierValue =
    cpfDigits.length === 11 ? cpfDigits : cpfRaw || undefined;

  return {
    patientId: params.lookupPatientId,
    officialName: fullName,
    phone,
    taxIdentifierValue,
    birthDate: "",
  };
}

/**
 * Carrega tutores (RelatedPerson) dos pets da organização e indexa por:
 * - `patientId` lógico do tutor (`{oids.cpf}-{cpf}`)
 * - `patientId` do pet (para colunas pet → tutor na UI)
 */
export async function fetchVetTutorSummariesByPetIds(
  petPatientIds: string[],
): Promise<{
  byLogicalPatientId: Map<string, CrmPatientSummary>;
  byPetPatientId: Map<string, CrmPatientSummary>;
}> {
  const byLogicalPatientId = new Map<string, CrmPatientSummary>();
  const byPetPatientId = new Map<string, CrmPatientSummary>();

  const ids = petPatientIds.map((id) => String(id ?? "").trim()).filter(Boolean);
  if (ids.length === 0) {
    return { byLogicalPatientId, byPetPatientId };
  }

  const response = await GetRelatedPerson({
    patient: ids.join(","),
    vetRelated: true,
  });

  const entries = (response?.data?.entry ?? []) as RelatedPersonBundleEntry[];

  for (const entry of entries) {
    const rp = entry.resource;
    const patientRef = String(rp?.patient?.reference ?? "").trim();
    if (!patientRef.startsWith("Patient/")) continue;

    const petId = patientRef.replace(/^Patient\//, "");
    const cpfRaw = pickTutorCpfFromIdentifiers(rp?.identifier);
    const cpfDigits = cpfRaw ? normalizeCpf(cpfRaw) : "";
    const logicalId =
      cpfDigits.length === 11 ? buildLogicalPatientIdFromCpfDigits(cpfDigits) : petId;

    const summary = mapRelatedPersonToTutorSummary({
      relatedPerson: rp,
      lookupPatientId: logicalId,
    });
    if (!summary) continue;

    byPetPatientId.set(petId, summary);
    byLogicalPatientId.set(logicalId, summary);

    // Garante lookup também pelo id exato vindo do Basic (pode variar formatação).
    if (cpfDigits.length === 11) {
      byLogicalPatientId.set(buildLogicalPatientIdFromCpfDigits(cpfDigits), summary);
    }
  }

  return { byLogicalPatientId, byPetPatientId };
}

/** Resumo mínimo quando o tutor ainda não foi resolvido via RelatedPerson. */
export function buildFallbackTutorSummaryFromPatientId(
  patientId: string,
): CrmPatientSummary {
  const cpfDigits = extractCpfDigitsFromPatientId(patientId);
  return {
    patientId,
    officialName: cpfDigits
      ? `Tutor (CPF ${cpfDigits})`
      : patientId,
    birthDate: "",
    taxIdentifierValue: cpfDigits,
  };
}
